import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  Button,
  Stack,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload,
  Folder,
  NightlightRound,
  LightMode,
  ExitToApp,
  Login as LoginIcon,
  Menu as MenuIcon,
  Close,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ThemeContext } from '../../theme/ThemeProvider';

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleColorMode } = React.useContext(ThemeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawer, setMobileDrawer] = useState(false);

  const handleLogout = async () => {
    await logout();
    setAnchorEl(null);
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navbar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(20px)',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
            {/* Mobile Menu */}
            {isMobile && (
              <IconButton onClick={() => setMobileDrawer(true)} edge="start" sx={{ color: 'text.primary' }}>
                <MenuIcon />
              </IconButton>
            )}

            {/* Brand */}
            <Typography
              component={Link}
              to="/"
              variant="h6"
              sx={{
                textDecoration: 'none',
                fontWeight: 800,
                fontSize: { xs: '1.05rem', sm: '1.2rem' },
                background: 'linear-gradient(135deg, #7C6FFF 0%, #FF6B8A 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              UploadMe
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Nav */}
            {!isMobile && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                {isAuthenticated && (
                  <Button
                    component={Link}
                    to="/files"
                    startIcon={<Folder sx={{ fontSize: 18 }} />}
                    size="small"
                    sx={{
                      color: location.pathname === '/files' ? 'primary.main' : 'text.secondary',
                      fontWeight: location.pathname === '/files' ? 600 : 500,
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    My Files
                  </Button>
                )}

                <Button
                  component={Link}
                  to="/"
                  variant={location.pathname === '/' ? 'contained' : 'text'}
                  startIcon={<CloudUpload sx={{ fontSize: 18 }} />}
                  size="small"
                  sx={{
                    ...(location.pathname !== '/' && {
                      color: 'text.secondary',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                    }),
                    ...(location.pathname === '/' && { px: 2.5 }),
                  }}
                >
                  Upload
                </Button>
              </Stack>
            )}

            {/* Right Section */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1 }}>
              <IconButton onClick={toggleColorMode} size="small" sx={{ color: 'text.secondary' }}>
                {mode === 'dark' ? <LightMode sx={{ fontSize: 20 }} /> : <NightlightRound sx={{ fontSize: 20 }} />}
              </IconButton>

              {isAuthenticated ? (
                <>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0, ml: 0.5 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        fontSize: 14,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #7C6FFF 0%, #FF6B8A 100%)',
                      }}
                    >
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={!!anchorEl}
                    onClose={() => setAnchorEl(null)}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    slotProps={{ paper: { sx: { mt: 1.5, minWidth: 200 } } }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="body2" fontWeight={700}>{user?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">Signed in</Typography>
                    </Box>
                    <Divider />
                    <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                      <ListItemIcon><ExitToApp fontSize="small" /></ListItemIcon>
                      Sign out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Tooltip_Login isMobile={isMobile} />
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawer}
        onClose={() => setMobileDrawer(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'background.paper',
            borderRight: 0,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              background: 'linear-gradient(135deg, #7C6FFF 0%, #FF6B8A 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            UploadMe
          </Typography>
          <IconButton onClick={() => setMobileDrawer(false)} size="small">
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ px: 1, pt: 1 }}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              selected={location.pathname === '/'}
              onClick={() => setMobileDrawer(false)}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}><CloudUpload /></ListItemIcon>
              <ListItemText primary="Upload" />
            </ListItemButton>
          </ListItem>
          {isAuthenticated && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/files"
                selected={location.pathname === '/files'}
                onClick={() => setMobileDrawer(false)}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}><Folder /></ListItemIcon>
                <ListItemText primary="My Files" />
              </ListItemButton>
            </ListItem>
          )}
          {!isAuthenticated && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/login"
                onClick={() => setMobileDrawer(false)}
                sx={{ borderRadius: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}><LoginIcon /></ListItemIcon>
                <ListItemText primary="Sign In" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>

      {/* Anonymous Banner */}
      {!isAuthenticated && (
        <Box
          sx={{
            background: alpha('#FBBF24', 0.06),
            borderBottom: `1px solid ${alpha('#FBBF24', 0.12)}`,
            py: 1,
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: '#FBBF24', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
            Anonymous uploads expire after 1 day.{' '}
            <Typography
              component={Link}
              to="/login"
              variant="body2"
              sx={{
                color: '#FF6B8A',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: 'inherit',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Log in to keep files longer
            </Typography>
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        <Box className="fade-in">{children}</Box>
      </Container>
    </Box>
  );
};

const Tooltip_Login: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
  <IconButton component={Link} to="/login" size="small" sx={{ color: 'text.secondary' }}>
    <LoginIcon sx={{ fontSize: 20 }} />
  </IconButton>
);

export default MainLayout;
